// Modular number formatting utility
// Ensures consistent decimal place display across the app

/**
 * Format number to 2 decimal places for display
 * Database values remain unchanged
 */
export function formatDecimal(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
}

/**
 * Format number to 2 decimal places as a number
 */
export function roundToTwo(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 0;
  return Math.round(num * 100) / 100;
}

/**
 * Format quantity for display (2 decimal places)
 */
export function formatQuantity(quantity: number | string): string {
  return formatDecimal(quantity);
}

/**
 * Format price for display (2 decimal places)
 */
export function formatPrice(price: number | string): string {
  if (price === null || price === undefined || price === '') return '0.00';
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num)) return '0.00';
  // Always exactly 2dp: the bare toLocaleString() this replaced honoured
  // NEITHER bound, so 299.5 rendered "299.5" and 2870.4000000000005 rendered
  // "2,870.4" on the pricing page, plan tables and voucher dialogs.
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Round a money value to currency precision (2dp).
 * Use at every point a computed amount is stored or written into a price
 * field — margin/markup math (cost / (1 - margin)) and per-unit divisions
 * produce repeating decimals that otherwise get persisted verbatim.
 */
export function roundMoney(value: number | string): number {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!isFinite(num as number)) return 0;
  return Math.round((num as number) * 100) / 100;
}
